import { getCssNumberValue } from "core/dom_utils";
import { getCssValueWithUnit } from "core/str_utils";

/**
 * @callback useResizeSaveElemWidth
 * @param {number} width
 * @return {void}
 */

/**
 * Хук содержит логику для изменения ширины столбца.
 * Возвращаемый обработчик onMouseDown надо навесить на resizer столбца
 * @param {object} props
 * @param {HTMLElement[]} props.resizableElems - массив элементов столбцов
 * @param {number} props.minWidth - минимальная ширина элемента
 * @param {useResizeSaveElemWidth} [props.saveElemWidth] - функция сохранения ширины элемента
 */
export const useResizeColumn = ({ resizableElems, minWidth, columnsSettings, saveColumnsSettings }) => {
	let handleMoveMouse;
	let handleUpMouse;
	let originalElemWidth = 0;
	let originalNextElemWidth = 0;
	let originalMouseX = 0;
	let elemMinWidth = 0;
	let hasElemAutoWidth = 0;
	let nextElemMinWidth = 0;
	let hasNextElemAutoWidth = false;

	const getElemWidth = (elem) => {
		const paddingLeft = getCssNumberValue(elem, "padding-left");
		const paddingRight = getCssNumberValue(elem, "padding-right");
		const width = elem.getBoundingClientRect().width - paddingLeft - paddingRight;
		return width;
	};

	function mouseDownHandler(clmIndex) {
		return function onMouseDown(e) {
			e.preventDefault();
			const elem = resizableElems[clmIndex];
			const nextElem = resizableElems[clmIndex + 1];

			if (!elem) return;

			elemMinWidth = parseFloat(elem.dataset.minWidth.replace("px", ""));
			hasElemAutoWidth = getComputedStyle(elem, null).getPropertyValue("width") === "auto";
			elem.style.maxWidth = "none";
			elem.style.minWidth = "none";
			elem.style.transition = "width 0ms";
			originalElemWidth = getElemWidth(elem);
			originalMouseX = e.pageX;

			if (nextElem) {
				nextElemMinWidth = parseFloat(nextElem.dataset.minWidth.replace("px", ""));
				hasNextElemAutoWidth = getComputedStyle(nextElem, null).getPropertyValue("width") === "auto";
				nextElem.style.maxWidth = "none";
				nextElem.style.minWidth = "none";
				nextElem.style.transition = "width 0ms";
				originalNextElemWidth = getElemWidth(nextElem);
			}

			handleMoveMouse = mouseMoveHandler(clmIndex);
			handleUpMouse = mouseUpHandler(clmIndex);

			window.addEventListener("mousemove", handleMoveMouse);
			window.addEventListener("mouseup", handleUpMouse);
		};
	}

	function mouseMoveHandler(clmIndex) {
		return function onMouseMove(e) {
			const elem = resizableElems[clmIndex];
			const nextElem = resizableElems[clmIndex + 1];

			if (!elem) return;

			const mouseXShift = e.pageX - originalMouseX;
			const elemWidth = originalElemWidth + mouseXShift;
			const nextElemWidth = originalNextElemWidth - mouseXShift;

			if (elemWidth >= Math.max(minWidth, elemMinWidth) && (nextElemWidth >= Math.max(minWidth, nextElemMinWidth) || !nextElem)) {
				if (!elemMinWidth && !hasElemAutoWidth) {
					elem.style.width = getCssValueWithUnit(elemWidth);
				}

				if (nextElem && !nextElemMinWidth && !hasNextElemAutoWidth) {
					nextElem.style.width = getCssValueWithUnit(nextElemWidth);
				}
			}
		};
	}

	function mouseUpHandler(clmIndex) {
		return function onMouseUp() {
			let nextElemWidth = 0;
			const elem = resizableElems[clmIndex];
			const nextElem = resizableElems[clmIndex + 1];

			if (!elem) return;

			elem.style.transition = "";
			const elemWidth = getCssNumberValue(elem, "width");

			if (nextElem) {
				nextElem.style.transition = "";
				nextElemWidth = getCssNumberValue(nextElem, "width");
			}

			if (saveColumnsSettings) {
				const elemClmName = elem.getAttribute("clm");
				const newColumnsSettings = {
					...(columnsSettings ? columnsSettings : {}),
					[elemClmName]: {
						...(columnsSettings?.[elemClmName] ? columnsSettings[elemClmName] : {}),
						width: hasElemAutoWidth ? "auto" : elemWidth && !elemMinWidth ? elemWidth : "",
					},
				};

				if (nextElem) {
					const nextElemClmName = nextElem.getAttribute("clm");
					newColumnsSettings[nextElemClmName] = {
						...(columnsSettings?.[nextElemClmName] ? columnsSettings[nextElemClmName] : {}),
						width: hasNextElemAutoWidth ? "auto" : nextElemWidth && !nextElemMinWidth ? nextElemWidth : "",
					};
				}

				saveColumnsSettings(newColumnsSettings);
			}

			window.removeEventListener("mousemove", handleMoveMouse);
			window.removeEventListener("mouseup", handleUpMouse);
		};
	}

	return { mouseDownHandler };
};
